import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Clock,
  Edit,
  Loader2,
  CheckCircle,
  Briefcase,
  Camera,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  DollarSign,
  UserCheck,
  Circle,
  AlertTriangle,
  Trash2,
} from 'lucide-react';
import { formatDate, formatTime, formatDuration, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import ImageLightbox from '../components/ImageLightbox';

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [clientData, setClientData] = useState(null);
  const [clientPhotos, setClientPhotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState([]);
  
  // Job detail modal state
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobDetail, setJobDetail] = useState(null);
  const [jobChecklist, setJobChecklist] = useState([]);
  const [jobPhotos, setJobPhotos] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await api().get(`/clients/${id}`);
      setClientData(response.data);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await api().delete(`/clients/${id}`);
      toast.success('Client deleted successfully');
      navigate('/clients');
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const fetchClientPhotos = async () => {
    if (clientPhotos) return;
    setPhotosLoading(true);
    try {
      const response = await api().get(`/clients/${id}/photos`);
      setClientPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch client photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setPhotosLoading(false);
    }
  };

  // Fetch full job details when clicking on a history item
  const openJobDetail = async (jobId) => {
    setSelectedJob(jobId);
    setJobLoading(true);
    setJobDetail(null);
    setJobChecklist([]);
    setJobPhotos([]);
    
    try {
      // Fetch booking details, checklist, and photos in parallel
      const [bookingRes, checklistRes, photosRes] = await Promise.all([
        api().get(`/bookings/${jobId}`),
        api().get(`/bookings/${jobId}/checklist`).catch(() => ({ data: { checklist: [] } })),
        api().get(`/bookings/${jobId}/photos`).catch(() => ({ data: [] }))
      ]);
      
      setJobDetail(bookingRes.data);
      setJobChecklist(checklistRes.data.checklist || []);
      setJobPhotos(photosRes.data || []);
      
      // Auto-expand all checklist groups
      const groups = [...new Set(checklistRes.data.checklist?.map(item => item.group).filter(Boolean))];
      const expanded = {};
      groups.forEach(g => expanded[g] = true);
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
      setSelectedJob(null);
    } finally {
      setJobLoading(false);
    }
  };

  const closeJobDetail = () => {
    setSelectedJob(null);
    setJobDetail(null);
    setJobChecklist([]);
    setJobPhotos([]);
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Open lightbox with photos
  const openLightbox = (photos, index = 0) => {
    const slides = photos.map((photo) => ({
      src: photo.fullUrl || `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
      alt: photo.photo_type || photo.item_task || 'Photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Open lightbox for job detail photos
  const openJobPhotoLightbox = (index = 0) => {
    const slides = jobPhotos.map((photo) => ({
      src: `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
      alt: photo.photo_type || 'Photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Group checklist items
  const getChecklistGroups = () => {
    const groups = {};
    jobChecklist.forEach(item => {
      const group = item.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    return groups;
  };

  // Group photos by type
  const getPhotosByType = () => {
    return jobPhotos.reduce((acc, photo) => {
      const type = photo.photo_type || 'other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(photo);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!clientData) {
    return null;
  }

  const { client, service_history, upcoming_bookings } = clientData;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/clients')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-zinc-900">{client.name}</h1>
              <Badge className={client.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-700'}>
                {client.status}
              </Badge>
            </div>
            <p className="text-zinc-500 text-sm mt-1">Client since {formatDate(client.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          {user?.role === 'director' && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-client-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Client
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{client?.name}</strong>? This action cannot be undone.
              {upcoming_bookings?.length > 0 && (
                <span className="block mt-2 text-amber-600">
                  Note: This client has {upcoming_bookings.length} active booking(s). You'll need to complete or cancel them first.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClient}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Things to Note Warning - Prominent Alert */}
      {client.things_to_note && (
        <Card className="border-2 border-red-300 bg-red-50" data-testid="things-to-note-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Things to Note
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-800 whitespace-pre-wrap">{client.things_to_note}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Client Info */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-zinc-500" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
                >
                  <Phone className="h-4 w-4" />
                  {client.phone}
                </a>
              )}
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
                >
                  <Mail className="h-4 w-4" />
                  {client.email}
                </a>
              )}
            </CardContent>
          </Card>

          {/* Address Info */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-zinc-500" />
                Addresses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-zinc-500">Service Address</p>
                <p className="text-zinc-900">{client.service_address}</p>
              </div>
              {client.billing_address && (
                <div>
                  <p className="text-sm text-zinc-500">Billing Address</p>
                  <p className="text-zinc-900">{client.billing_address}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Notes & Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {client.access_instructions && (
                <div>
                  <p className="text-sm text-zinc-500">Access Instructions</p>
                  <p className="text-zinc-700">{client.access_instructions}</p>
                </div>
              )}
              {client.pets && (
                <div>
                  <p className="text-sm text-zinc-500">Pets</p>
                  <p className="text-zinc-700">{client.pets}</p>
                </div>
              )}
              {client.preferred_days && client.preferred_days.length > 0 && (
                <div>
                  <p className="text-sm text-zinc-500">Preferred Days</p>
                  <p className="text-zinc-700">{client.preferred_days.join(', ')}</p>
                </div>
              )}
              {client.preferred_times && (
                <div>
                  <p className="text-sm text-zinc-500">Preferred Times</p>
                  <p className="text-zinc-700">{client.preferred_times}</p>
                </div>
              )}
              {client.notes && (
                <div>
                  <p className="text-sm text-zinc-500">General Notes</p>
                  <p className="text-zinc-700">{client.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - History & Bookings */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="upcoming" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upcoming" data-testid="upcoming-tab">
                Upcoming ({upcoming_bookings.length})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="history-tab">
                History ({service_history.length})
              </TabsTrigger>
              <TabsTrigger value="photos" data-testid="photos-tab" onClick={fetchClientPhotos}>
                Photos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-3">
              {upcoming_bookings.length === 0 ? (
                <Card className="border-zinc-100">
                  <CardContent className="py-8 text-center">
                    <Calendar className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No upcoming bookings</p>
                    <Link to={`/bookings/new?client=${id}`}>
                      <Button className="mt-4" data-testid="create-booking-btn">
                        Create Booking
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                upcoming_bookings.map((booking) => (
                  <Card
                    key={booking.id}
                    className="border-zinc-100 hover:border-zinc-200 cursor-pointer transition-colors"
                    onClick={() => openJobDetail(booking.id)}
                    data-testid={`upcoming-item-${booking.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusLabel(booking.status)}
                            </Badge>
                          </div>
                          <p className="font-medium text-zinc-900">
                            {formatDate(booking.scheduled_date)}
                          </p>
                          <p className="text-sm text-zinc-500">
                            {booking.time_window_start} - {booking.time_window_end}
                          </p>
                        </div>
                        <Briefcase className="h-5 w-5 text-zinc-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="history" className="space-y-3">
              {service_history.length === 0 ? (
                <Card className="border-zinc-100">
                  <CardContent className="py-8 text-center">
                    <Clock className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No service history yet</p>
                  </CardContent>
                </Card>
              ) : (
                service_history.map((visit) => (
                  <Card 
                    key={visit.id} 
                    className="border-zinc-100 hover:border-zinc-200 cursor-pointer transition-colors"
                    onClick={() => openJobDetail(visit.id)}
                    data-testid={`history-item-${visit.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                            <span className="font-medium text-zinc-900">
                              {formatDate(visit.scheduled_date)}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-500">
                            Duration: {formatDuration(visit.total_duration_minutes)}
                          </p>
                          {visit.job_details && (
                            <p className="text-sm text-zinc-500 mt-1 line-clamp-1">{visit.job_details}</p>
                          )}
                        </div>
                        <Briefcase className="h-5 w-5 text-zinc-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="photos" className="space-y-4">
              {photosLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
                </div>
              ) : !clientPhotos || clientPhotos.jobs_with_photos?.length === 0 ? (
                <Card className="border-zinc-100">
                  <CardContent className="py-8 text-center">
                    <Camera className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No photos from completed jobs yet</p>
                  </CardContent>
                </Card>
              ) : (
                clientPhotos.jobs_with_photos.map((job) => {
                  const allPhotos = [
                    ...job.booking_photos.map(p => ({
                      ...p,
                      fullUrl: `${process.env.REACT_APP_BACKEND_URL}${p.url}`
                    })),
                    ...job.item_photos.map(p => ({
                      ...p,
                      fullUrl: `${process.env.REACT_APP_BACKEND_URL}${p.url}`
                    }))
                  ];
                  
                  return (
                    <Card key={job.booking_id} className="border-zinc-100">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-zinc-500" />
                            {formatDate(job.scheduled_date)}
                          </CardTitle>
                          <Badge variant="outline">{allPhotos.length} photos</Badge>
                        </div>
                        {job.package_name && (
                          <p className="text-sm text-zinc-500">{job.package_name}</p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {allPhotos.map((photo, idx) => (
                            <div 
                              key={photo.id || idx}
                              className="aspect-square rounded-lg bg-zinc-100 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openLightbox(allPhotos, idx)}
                              data-testid={`client-photo-${job.booking_id}-${idx}`}
                            >
                              <img
                                src={photo.fullUrl}
                                alt={photo.photo_type || photo.item_task || 'Job photo'}
                                className="h-full w-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          ))}
                        </div>
                        {/* Photo type breakdown */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {job.booking_photos.filter(p => p.photo_type === 'before').length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              Before: {job.booking_photos.filter(p => p.photo_type === 'before').length}
                            </Badge>
                          )}
                          {job.booking_photos.filter(p => p.photo_type === 'during').length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              During: {job.booking_photos.filter(p => p.photo_type === 'during').length}
                            </Badge>
                          )}
                          {job.booking_photos.filter(p => p.photo_type === 'after').length > 0 && (
                            <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
                              After: {job.booking_photos.filter(p => p.photo_type === 'after').length}
                            </Badge>
                          )}
                          {job.booking_photos.filter(p => p.photo_type === 'issue').length > 0 && (
                            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                              Issues: {job.booking_photos.filter(p => p.photo_type === 'issue').length}
                            </Badge>
                          )}
                          {job.item_photos.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              Checklist: {job.item_photos.length}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Job Detail Modal - Opens when clicking on history/upcoming items */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && closeJobDetail()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-zinc-500" />
              Job Details
            </DialogTitle>
          </DialogHeader>
          
          {jobLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </div>
          ) : jobDetail ? (
            <div className="space-y-6">
              {/* Job Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-zinc-500">Date</p>
                  <p className="font-medium">{formatDate(jobDetail.scheduled_date)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-zinc-500">Status</p>
                  <Badge className={getStatusColor(jobDetail.status)}>
                    {getStatusLabel(jobDetail.status)}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-zinc-500">Time Window</p>
                  <p className="font-medium">
                    {jobDetail.time_window_start || 'TBC'} - {jobDetail.time_window_end || 'TBC'}
                  </p>
                </div>
                {jobDetail.price && (
                  <div className="space-y-1">
                    <p className="text-sm text-zinc-500">Price</p>
                    <p className="font-medium flex items-center gap-1">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                      ${jobDetail.price.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {/* Timing Info (for completed jobs) */}
              {(jobDetail.checkin_time || jobDetail.checkout_time) && (
                <Card className="border-zinc-100 bg-zinc-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-zinc-500" />
                      <span className="font-medium text-zinc-700">Timing</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      {jobDetail.checkin_time && (
                        <div>
                          <p className="text-zinc-500">Arrived</p>
                          <p className="font-medium text-emerald-600">{formatTime(jobDetail.checkin_time)}</p>
                        </div>
                      )}
                      {jobDetail.checkout_time && (
                        <div>
                          <p className="text-zinc-500">Departed</p>
                          <p className="font-medium text-emerald-600">{formatTime(jobDetail.checkout_time)}</p>
                        </div>
                      )}
                      {jobDetail.total_time_on_site_minutes && (
                        <div>
                          <p className="text-zinc-500">Duration</p>
                          <p className="font-medium">{formatDuration(jobDetail.total_time_on_site_minutes)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assigned Staff */}
              {jobDetail.assigned_staff && (
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <UserCheck className="h-5 w-5 text-zinc-500" />
                  <div>
                    <p className="text-sm text-zinc-500">Assigned Staff</p>
                    <p className="font-medium">{jobDetail.assigned_staff.full_name}</p>
                  </div>
                </div>
              )}

              {/* Job Notes */}
              {jobDetail.job_details && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-zinc-500" />
                    <span className="font-medium text-zinc-700">Job Notes</span>
                  </div>
                  <p className="text-zinc-600 text-sm bg-zinc-50 p-3 rounded-lg">
                    {jobDetail.job_details}
                  </p>
                </div>
              )}

              {/* Staff Notes */}
              {jobDetail.staff_notes && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-blue-700">Staff Notes</span>
                  </div>
                  <p className="text-blue-800 text-sm bg-blue-50 p-3 rounded-lg border border-blue-200">
                    {jobDetail.staff_notes}
                  </p>
                </div>
              )}

              {/* Checklist */}
              {jobChecklist.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-zinc-500" />
                      <span className="font-medium text-zinc-700">Checklist</span>
                      {jobDetail.package_name_snapshot && (
                        <Badge variant="outline" className="text-xs">
                          {jobDetail.package_name_snapshot}
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-zinc-500">
                      {jobChecklist.filter(i => i.completed).length}/{jobChecklist.length} completed
                    </span>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(getChecklistGroups()).map(([group, items]) => (
                      <div key={group} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleGroup(group)}
                          className="w-full flex items-center justify-between p-3 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
                        >
                          <span className="font-medium text-zinc-700 text-sm">{group}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-500">
                              {items.filter(i => i.completed).length}/{items.length}
                            </span>
                            {expandedGroups[group] ? (
                              <ChevronUp className="h-4 w-4 text-zinc-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-zinc-400" />
                            )}
                          </div>
                        </button>
                        {expandedGroups[group] && (
                          <div className="divide-y">
                            {items.map((item) => (
                              <div
                                key={item.id}
                                className={`p-3 flex items-start gap-3 text-sm ${
                                  item.completed ? 'bg-emerald-50/50' : ''
                                }`}
                              >
                                {item.completed ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Circle className="h-4 w-4 text-zinc-300 mt-0.5 flex-shrink-0" />
                                )}
                                <span className={item.completed ? 'text-zinc-500 line-through' : 'text-zinc-700'}>
                                  {item.task}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photos */}
              {jobPhotos.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Camera className="h-4 w-4 text-zinc-500" />
                    <span className="font-medium text-zinc-700">Photos</span>
                    <Badge variant="outline" className="text-xs">{jobPhotos.length}</Badge>
                  </div>
                  
                  {/* Photos by type */}
                  {Object.entries(getPhotosByType()).map(([type, photos]) => (
                    <div key={type} className="mb-4">
                      <p className="text-sm text-zinc-500 capitalize mb-2">{type}</p>
                      <div className="grid grid-cols-4 gap-2">
                        {photos.map((photo, idx) => (
                          <div
                            key={photo.id}
                            className="aspect-square rounded-lg bg-zinc-100 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              // Find the global index for this photo
                              const globalIndex = jobPhotos.findIndex(p => p.id === photo.id);
                              openJobPhotoLightbox(globalIndex);
                            }}
                          >
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                              alt={type}
                              className="h-full w-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        index={lightboxIndex}
        slides={lightboxSlides}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default ClientDetailPage;
